import React, { useState, useEffect } from 'react'
import { toast } from 'react-toastify';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import Maybe from 'components/__new/common/Maybe';
import { ReactComponent as IconTimes } from "assets/images/icons/project-icons/times.svg"
import { ReactComponent as IconBack } from "assets/images/icons/project-icons/arrow_back.svg"
import * as approvalAction from "store/entities/approvals/action"
import FormDrawer from 'components/__new/common/form-drawer';
import EditWorkflowStage from './EditWorkflowStage';
import ModalAlert from 'components/__new/common/ModalAlert';
import EditWorkflowDetails from './EditWorkflowDetails';
import { ButtonLoader } from 'components/__new/common/form';
import useLoginInfo from 'hooks/useLoginInfo';
import * as notificationAction from "store/entities/notification/action";


const EditWorkflow = ({isOpen, setIsOpen, workflow}) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const {userInfo} = useLoginInfo();
    const { updatedWorkflowLoading, updatedWorkflow } = useSelector(s => s.entities.approvals);
    const [currentStep, setCurrentStep] = useState(1);
    const [validationError, setValidationError] = useState(null);
    const [workflowPayload, setWorkflowPayload] = useState(null);
    const [isOpenModalAlertSuccess, setIsOpenModalAlertSuccess] = useState(false);
    const isCreatingDetails = currentStep === 1;
    const defaultForm = {
        title: `${workflow?.title}`,
        description: `${workflow?.description}`,
        type: `${workflow?.type}`,
        stages: workflow?.WorkflowStages?.map(stage => ({
            name: stage?.name,
            approverRoleId: stage?.approvalRole?.id,
            id: stage?.id
        }))
    }
    const [form, setForm] = useState(defaultForm);
    const onProceed = () => {
        if(isCreatingDetails) {
            if(form?.title?.length < 1 || form?.description?.length < 1 || form?.type?.length < 1) {
                toast.error("Please fill in all fields")
            } else {
                setWorkflowPayload({ title: form?.title, description: form?.description, type: form?.type });
                setCurrentStep(2);
            }
        } else if(currentStep === 2) {
            if(form?.stages?.length > 0 && form?.stages[0].name?.length < 1) {
                toast.error("Please fill out stage name");
            } else if(form?.stages?.length > 0 && form?.stages[0]?.approverRoleId?.length < 1) {
                toast.error("Please select approval role")
            } else {
                const payload = {
                    ...workflowPayload,
                    stages: form?.stages?.map(stage => ({
                        name: stage?.name,
                        approverRoleId: stage?.approverRoleId,
                        id: stage?.id
                    }))
                }

                for (let key in payload) {
                    if (payload.hasOwnProperty(key) && payload[key] === "") {
                        delete payload[key];
                    }
                }

                setWorkflowPayload(payload);
                const workflowId = workflow?.id;
                dispatch(approvalAction.updateApprovalWorkflowStage(payload, workflowId))
            }
        }
    }

    const onCancel = () => {
        if(isCreatingDetails) {
            setIsOpen(false);
        } else {
            setCurrentStep(1);
        }
    }

    useEffect(() => {
        setValidationError(null);
        setForm(defaultForm)
        // eslint-disable-next-line
    }, [])
    
    useEffect(() => {
        if (updatedWorkflow?.error === false){
            history.push("/approvals/workflow")
            setIsOpen(false);
            setIsOpenModalAlertSuccess(true);
            dispatch(approvalAction.resetUpdatedWorkflow());
            setCurrentStep(1)
            dispatch(notificationAction.getAllNotifications());
        }
    }, [updatedWorkflow, dispatch]);

    useEffect(() => {
        dispatch(approvalAction.getRoles({employerId: userInfo?.employer?._id}));
    }, [dispatch]);

    const disableFirstStepSubmit = currentStep === 1 && (!form?.title?.length || !form?.description?.length || !form?.type?.length);

    return (
        <div className="w-full">
            <FormDrawer display="center" size="md" isOpen={isOpen} setIsOpen={setIsOpen}>
                <div className='w-full p-7'>
                    <div className='pb-2'>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center pb-1">
                                <Maybe condition={!isCreatingDetails}>
                                    <div className="w-7 h-7 flex items-center justify-center mr-3 bg-[#f4f4f4] rounded-md">
                                        <IconBack className='cursor-pointer' onClick={() => setCurrentStep(1)} />
                                    </div>
                                </Maybe>
                                
                                <div className="text-xl font-bold">
                                    Edit Workflow
                                </div>
                            </div>
                            
                            <div onClick={() => setIsOpen(false)} className='w-7 h-7 bg-[#f4f4f4] cursor-pointer rounded-md flex items-center justify-center'>
                                <IconTimes width={30} />
                            </div>
                        </div>
                        
                        <div className="pt-1">
                            Make changes to an approval workflow
                        </div>
                        
                        <div className="w-full flex items-center justify-between mt-5">
                            <div className="w-[47%]">
                                <div className='w-full h-1.5 rounded-lg bg-ep-primary-600' />
                                <p className="mb-0 text-ep-gray-700 pt-1">Workflow Details</p>
                            </div>
                            
                            <div className="w-[47%]">
                                <div className={`w-full h-1.5 rounded-lg ${isCreatingDetails ? "bg-[#e5e7eb]" : "bg-ep-primary-600"}`} />
                                <p className={`mb-0 ${isCreatingDetails ? "text-ep-gray-500" : "text-ep-gray-700"} pt-1`}>Workflow Stages</p>
                            </div>
                        </div>
                    </div>
                    
                    <Maybe condition={currentStep === 1}>
                        <EditWorkflowDetails
                            validationError={validationError}
                            setValidationError={setValidationError}
                            form={form}
                            setForm={setForm}
                            defaultForm={defaultForm}
                            workflow={workflow}
                        />
                    </Maybe>
                    
                    <Maybe condition={currentStep === 2}>
                        <EditWorkflowStage
                            validationError={validationError}
                            form={form}
                            setForm={setForm}
                            defaultForm={defaultForm}
                            workflow={workflow}
                        />
                    </Maybe>
                
                    <div className="w-full flex items-center justify-end mt-7 pt-5">
                        <button type="button" onClick={onCancel} className="btn btn-transparent-primary mr-3">
                            {isCreatingDetails ? "Cancel" : "Back"}
                        </button>

                        <ButtonLoader loading={updatedWorkflowLoading} disabled={disableFirstStepSubmit} onClick={onProceed} type="button" className="btn btn-ep-primary">
                            {currentStep === 2 ? "Save Changes" : "Continue"}
                        </ButtonLoader>
                    </div>
                </div>
            </FormDrawer>
            
            <ModalAlert
                successText="Changes saved successfully"
                dismissText="Close"
                isOpenModalAlert={isOpenModalAlertSuccess}
                setIsOpenModalAlert={setIsOpenModalAlertSuccess}
            />
        </div>
    )
}


export default EditWorkflow